import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, ClassroomSettingsDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import {
  getTemperatureUnit,
  setTemperatureUnit,
  type TemperatureUnit,
} from './temperatureUnitStorage.js';
import styles from './settingsForm.module.css';

const UNIT_OPTIONS: { value: TemperatureUnit; label: string; hint: string }[] = [
  { value: 'F', label: 'Fahrenheit', hint: '°F' },
  { value: 'C', label: 'Celsius', hint: '°C' },
  { value: 'words', label: 'Descriptive words', hint: 'Cold / Cool / Warm / Hot' },
];

export function WeatherPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;

  const settingsQuery = useQuery<ClassroomSettingsDto>({
    queryKey: ['classroom-settings', classroomId],
    queryFn: () => api.home.getSettings(classroomId),
  });

  const settings = settingsQuery.data;
  const [zipCode, setZipCode] = useState(settings?.zipCode ?? '');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error' | 'invalid'>('idle');
  const [unit, setUnit] = useState<TemperatureUnit>(() => getTemperatureUnit(classroomId));

  useEffect(() => {
    if (settings) setZipCode(settings.zipCode);
  }, [settings, classroomId]);

  useEffect(() => {
    setUnit(getTemperatureUnit(classroomId));
  }, [classroomId]);

  function chooseUnit(next: TemperatureUnit) {
    setUnit(next);
    setTemperatureUnit(classroomId, next);
  }

  const save = useMutation({
    mutationFn: (next: string) => api.home.patchSettings(classroomId, { zipCode: next }),
    onMutate: () => setStatus('saving'),
    onSuccess: (next) => {
      queryClient.setQueryData<ClassroomSettingsDto>(['classroom-settings', classroomId], next);
      setStatus('saved');
    },
    onError: () => setStatus('error'),
  });

  const onBlur = () => {
    if (!settings) return;
    if (zipCode === settings.zipCode) return;
    // Empty clears the field. Otherwise must be a 5-digit US zip.
    if (zipCode !== '' && !/^\d{5}$/.test(zipCode)) {
      setStatus('invalid');
      return;
    }
    save.mutate(zipCode);
  };

  const reset = () => save.mutate('');

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Weather</h2>
          <p className={styles.description}>
            The local-temperature display reads from OpenWeatherMap using the zip code below. Leave
            empty to hide the local temperature line.
          </p>
        </div>
        <ResetSettingsButton pageLabel="Weather" onReset={reset} disabled={save.isPending} />
      </header>

      <div className={styles.form}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="weather-zip">
            Zip code
          </label>
          <input
            id="weather-zip"
            className={styles.input}
            type="text"
            inputMode="numeric"
            pattern="\d{5}"
            maxLength={5}
            value={zipCode}
            placeholder="e.g. 90210"
            disabled={settingsQuery.isLoading}
            onChange={(e) => {
              setZipCode(e.target.value);
              if (status === 'invalid') setStatus('idle');
            }}
            onBlur={onBlur}
          />
          <span className={styles.hint}>
            5-digit US zip. Local temperature refreshes server-side every 30 minutes.
          </span>
          {status === 'invalid' ? (
            <span className={`${styles.statusLine} ${styles.statusError}`}>
              Zip must be 5 digits.
            </span>
          ) : status === 'saving' ? (
            <span className={styles.statusLine}>Saving…</span>
          ) : status === 'saved' ? (
            <span className={styles.statusLine}>Saved</span>
          ) : status === 'error' ? (
            <span className={`${styles.statusLine} ${styles.statusError}`}>Save failed</span>
          ) : null}
        </div>

        {/* Temperature display unit — affects the live thermometer
         *  readout, the "right now outside" card, and the weather
         *  history table. Use "Descriptive words" for younger grades
         *  so they associate the gauge position with a feeling word. */}
        <fieldset className={styles.field}>
          <legend className={styles.label}>How to show temperature</legend>
          <div className={styles.dayPicker}>
            {UNIT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.dayChip} ${unit === opt.value ? styles.dayChipActive : ''}`}
                onClick={() => chooseUnit(opt.value)}
                aria-pressed={unit === opt.value}
                data-testid={`weather-unit-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <span className={styles.hint}>
            Currently showing as <strong>{UNIT_OPTIONS.find((o) => o.value === unit)?.hint}</strong>
            .
          </span>
        </fieldset>
      </div>
    </div>
  );
}
